import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import PaymentTrackActions from "../../Redux/transition/payment-track/payment-track.reducer";
import DefMembershipActions from "../../Redux/actions/defMembership-actions";
import { Button } from 'primereact/button';
import "../../Styles/pages/payment.scss";
import { useReactToPrint } from 'react-to-print';
import logo from "../../assets/images/Reveknew_Logo_Colour_and_Black.png";
import Loading from "../../Components/Loading";
import { useDateFormatter } from "../../Components/FormatDateTime";
import { removePlusFromNum } from '../../Components/CleanInputs';
import { FormatCurrency, useCurrencyConstants } from '../../Components/CurrencyFormatter';
import AutoBillingBadge from '../../Components/AutoBillingBadge';

const PaymentDetail = () => {

    const { formatDate, formatDateTime } = useDateFormatter();

    const componentRef = useRef(null);
    const { t } = useTranslation("common");
    const { id: paymentTrackId } = useParams();

    const newDate = formatDate(new Date());

    const reactToPrintContent = React.useCallback(() => {
        return componentRef.current;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [componentRef.current]);

    const handlePrint = useReactToPrint({
        content: reactToPrintContent
    });

    const [state, setState] = useState({
        paymentTrack: null
    })

    //Redux State from Store
    const defMembership = useSelector((state) => state.defMembership.defMembership);
    const paymentTrack = useSelector((state) => state.paymentTracks.paymentTrack);
    const fetchingOnePaymentTrack = useSelector((state) => state.paymentTracks.fetchingOne);

    //Redux Dispatch Actions
    const dispatch = useDispatch();
    const getDefMembership = useCallback(() => { dispatch(DefMembershipActions.defMembershipRequest()); }, [dispatch])
    const getPaymentTrack = useCallback((paymentTrackId) => dispatch(PaymentTrackActions.paymentTrackRequest(paymentTrackId)), [dispatch]);

    const { currencyFormat, currencySymbol } = useCurrencyConstants();

    useEffect(() => {
        if (!defMembership) {
            getDefMembership();
        }
        if (defMembership) {
            getPaymentTrack(paymentTrackId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (paymentTrack) {
            setState((state) => { return { ...state, paymentTrack } })
        }
    }, [paymentTrack])

    if (fetchingOnePaymentTrack) {
        return <Loading />
    }

    return (
        <div >
            <Button label="Print" icon="pi pi-print"
                onClick={handlePrint}
                id="print_btn" />

            <div className={"section-to-print print_section"} ref={componentRef}>
                <div className='bus_info_main_div'>
                    <div className="div_logo_side">
                        <img src={defMembership.business.logoUrl} alt='' className="businessLogoStyle" />
                        <span className='invoice_txt'>{t('payments.receipt')}</span>
                    </div>
                    <div className='bus_info_div'>
                        <span className='bus_name'>{state.paymentTrack?.business.name}</span>
                        <span className='bus_info busNo'>{removePlusFromNum(state.paymentTrack?.business.phoneNo)}</span>
                        <span className='bus_info busEmail'>{state.paymentTrack?.business.email}</span>
                        <span className='bus_info'>{state.paymentTrack?.business.addressLine1} </span>
                    </div>
                </div>

                <div className='mt-4'>
                    <div className="grid subscriber-info-header">
                        <div className="col-4 md:col-4 lg:col-4 plan_description">{t("payments.date")}</div>
                        <div className="col-4 md:col-4 lg:col-4 plan_description">{t("payments.customer_info")}</div>
                    </div>

                    <div className="grid subscriber-info-div">
                        <div className="col-4 md:col-4 lg:col-4 plan_description">
                            <span className='cus_info plan_description'>{formatDateTime(state.paymentTrack?.receivedAt)}</span>
                        </div>

                        <div className="col-4 md:col-4 lg:col-4 plan_description display-ref">
                            {state.paymentTrack?.subscriber.businessCustomer.name ? <span className='cus_info plan_description'>{state.paymentTrack?.subscriber.businessCustomer.name}</span> : null}
                            {state.paymentTrack?.subscriber.businessCustomer.phoneNo ? <span className='cus_info cusNo plan_description'>{removePlusFromNum(state.paymentTrack?.subscriber.businessCustomer.phoneNo)}</span> : null}
                            {state.paymentTrack?.subscriber.businessCustomer.email ? <span className='plan_description'>{state.paymentTrack?.subscriber.businessCustomer.email}</span> : null}
                        </div>

                        <div className="col-4 md:col-4 lg:col-4 display-ref">
                            <span className="plan_description mb-2">{t("payments.customer_id")}{":"}{" "}{state.paymentTrack?.subscriber.businessCustomer.reference}</span>

                            {state.paymentTrack?.subscriber.businessCustomer.customerNum ? <div>
                                <span className="plan_description">{t("payments.customer_reference")}{":"}{" "}</span>
                                <span className="plan_description" id="businessCustomerNum">{state.paymentTrack?.subscriber.businessCustomer.customerNum}</span></div> : null}
                        </div>
                    </div>
                </div>

                <div className="paid-txt-div">
                    <span className="paid-txt">{t("payments.paid")}</span>
                </div>

                <div className="border1">
                    <div className="border2">
                        {state.paymentTrack?.plan.description ?
                            <div className="grid des-main-div">
                                <div className="col-6 md:col-6 lg:col-6 plan_description_main_div">
                                    {state.paymentTrack?.plan.name && (
                                        <div className="flex align-items-center gap-2">
                                            <span className="plan_name_description_margin">{state.paymentTrack?.plan.name}</span>
                                            <AutoBillingBadge collectionMode={state.paymentTrack?.plan.collectionMode} />
                                        </div>
                                    )}
                                    {state.paymentTrack?.plan.description ? <span className="plan_description">{state.paymentTrack?.plan.description}</span> : null}
                                </div>
                                <div className="col-6 md:col-6 lg:col-6">
                                    <FormatCurrency
                                        id="payment_main_amount_margin"
                                        amount={state.paymentTrack?.originalAmount}
                                        currencySymbol={currencySymbol}
                                        currencyFormat={currencyFormat}
                                        className="payment_amount"
                                    />
                                </div>
                            </div> :
                            <div className="grid des-main-div">
                                <div className="col-6 md:col-6 lg:col-6 centered-col">
                                    <div className="flex align-items-center gap-2">
                                        <span className="plan_name_description">{state.paymentTrack?.plan.name}</span>
                                        <AutoBillingBadge collectionMode={state.paymentTrack?.plan.collectionMode} />
                                    </div>
                                </div>
                                <div className="col-6 md:col-6 lg:col-6">
                                    <FormatCurrency
                                        id="payment_main_amount"
                                        amount={state.paymentTrack?.originalAmount}
                                        currencySymbol={currencySymbol}
                                        currencyFormat={currencyFormat}
                                        className="payment_amount"
                                    />
                                </div>
                            </div>}
                    </div>

                    {state.paymentTrack?.setupAmount !== null &&
                        <div className="border2">
                            <div className="grid setup-amt-div" >
                                <div className="col-6 md:col-6 lg:col-6">
                                    <span className="payments-setup">{t("payments.setup_fee")}</span>
                                </div>
                                <div className="col-6 md:col-6 lg:col-6">
                                    <FormatCurrency
                                        amount={state.paymentTrack?.setupAmount}
                                        currencySymbol={currencySymbol}
                                        currencyFormat={currencyFormat}
                                        className="setupAmount"
                                    />
                                </div>
                            </div>
                        </div>
                    }
                </div>
                <div className="grid mt-4 ml-4">
                    <div className="col-6 md:col-6 lg:col-6">
                        <span className="total-label">{t("payments.total")}</span>
                    </div>
                    <div className="col-6 md:col-6 lg:col-6">
                        <FormatCurrency
                            id="flex-direction"
                            amount={state.paymentTrack?.amount}
                            currencySymbol={currencySymbol}
                            currencyFormat={currencyFormat}
                            className="payments-total"
                        />
                    </div>
                </div>

                <hr className='hr_tag' />

                <div className="grid mt-6">
                    <div className="col-6 md:col-6 lg:col-6" >
                        <div className="grid span_divs reference_div mr-1">
                            <div className="col-6 md:col-6 lg:col-6 left_span_divs"><span className="left_spans">{t("payments.payment_id_caps")}</span></div>
                            <div className="col-6 md:col-6 lg:col-6 right_span_divs"><span className="right_spans">{state.paymentTrack?.reference}</span> </div>
                        </div>
                        <div className="grid span_divs mr-1 mt-4">
                            <div className="col-6 md:col-6 lg:col-6 left_span_divs"><span className="left_spans">{t("payments.method")}</span></div>
                            <div className="col-6 md:col-6 lg:col-6 right_span_divs"><span className="right_spans">{state.paymentTrack?.paymentMode}</span></div>
                        </div>
                    </div>

                </div>
                <div className='generation-div mt-6' style={{ textAlign: "right" }}>
                    <span className='mr-2'>{t("payments.generation_txt")}{":"}</span>
                    <img src={logo} alt="babylon-layout" className='generation_img' />{"-"}{" "}{newDate}
                </div>
            </div>
        </div >
    )
}

export default PaymentDetail;